import { useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { User, Lock, Phone, Mail, ShieldCheck, Save, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, refetch } = useAuth() as any;

  // 基本信息
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [savingProfile, setSavingProfile] = useState(false);

  // 修改密码
  const [oldPwd, setOldPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const res = await fetch("/api/auth/update-profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), phone: phone.trim() }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "更新失败");
      toast.success("个人信息已更新");
      refetch?.();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const changePassword = async () => {
    if (newPwd.length < 6) { toast.error("新密码至少6位"); return; }
    if (newPwd !== confirmPwd) { toast.error("两次输入的密码不一致"); return; }
    setSavingPwd(true);
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ oldPassword: oldPwd, newPassword: newPwd }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "修改失败");
      toast.success("密码已更新，下次登录生效");
      setOldPwd(""); setNewPwd(""); setConfirmPwd("");
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 p-4">
      <div className="flex items-center gap-3">
        <User className="h-5 w-5 text-primary" />
        <h1 className="text-xl font-bold">个人中心</h1>
      </div>

      {/* 头像 + 账号信息 */}
      <Card className="bg-card border-border/50">
        <CardContent className="p-5 flex items-center gap-4">
          <Avatar className="h-16 w-16 border-2 border-primary/20">
            <AvatarFallback className="text-xl font-bold bg-primary/10 text-primary">
              {(user?.name || user?.email || "U").charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-base">{user?.name || "未设置昵称"}</p>
            <p className="text-sm text-muted-foreground mt-0.5">{user?.email || user?.phone || ""}</p>
            <div className="flex items-center gap-2 mt-1.5">
              <Badge variant={user?.role === "admin" ? "default" : "secondary"} className="text-[10px]">
                <ShieldCheck className="h-3 w-3 mr-1" />
                {user?.role === "admin" ? "管理员" : "普通用户"}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 基本信息 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />基本信息
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">邮箱</Label>
            <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-muted/30 border border-border/40">
              <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
              <span className="text-sm">{user?.email || "未绑定"}</span>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground" htmlFor="name">昵称</Label>
            <Input id="name" value={name} onChange={e => setName(e.target.value)}
              placeholder="请输入昵称" className="h-9" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground" htmlFor="phone">手机号</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input id="phone" value={phone} onChange={e => setPhone(e.target.value)}
                placeholder="请输入手机号" className="h-9 pl-9" inputMode="tel" />
            </div>
          </div>
          <Button size="sm" onClick={saveProfile} disabled={savingProfile} className="gap-1.5">
            <Save className="h-3.5 w-3.5" />
            {savingProfile ? "保存中..." : "保存信息"}
          </Button>
        </CardContent>
      </Card>

      {/* 修改密码 */}
      <Card className="bg-card border-border/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Lock className="h-4 w-4 text-primary" />修改密码
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">当前密码</Label>
            <div className="relative">
              <Input type={showOld ? "text" : "password"} value={oldPwd}
                onChange={e => setOldPwd(e.target.value)}
                placeholder="请输入当前密码" className="h-9 pr-10" />
              <button type="button" onClick={() => setShowOld(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showOld ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <Separator />
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">新密码（至少 6 位）</Label>
            <div className="relative">
              <Input type={showNew ? "text" : "password"} value={newPwd}
                onChange={e => setNewPwd(e.target.value)}
                placeholder="请输入新密码" className="h-9 pr-10" />
              <button type="button" onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">确认新密码</Label>
            <Input type="password" value={confirmPwd}
              onChange={e => setConfirmPwd(e.target.value)}
              placeholder="再次输入新密码" className="h-9"
              style={{ borderColor: confirmPwd && confirmPwd !== newPwd ? "rgb(239,68,68)" : "" }} />
            {confirmPwd && confirmPwd !== newPwd && (
              <p className="text-xs text-destructive">两次密码不一致</p>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={changePassword}
            disabled={savingPwd || !newPwd || newPwd !== confirmPwd} className="gap-1.5">
            <Lock className="h-3.5 w-3.5" />
            {savingPwd ? "修改中..." : "修改密码"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
